import React, { useState, useEffect } from 'react';
import { X, Target, AlertCircle, Calendar, Info } from 'lucide-react';
import { goalService } from '../../services/goalService';
import { useAuth } from '../../contexts/AuthContext';
import { hasAnyRole } from '../../utils/permissions';

/**
 * CreateGoalModal - Modal for creating/editing goals
 *
 * Props:
 * - isOpen: boolean
 * - onClose: function()
 * - onSuccess: function(goal) - Called after successful creation
 * - editGoal: Goal object (optional) - If provided, edit mode
 */
const CreateGoalModal = ({
  isOpen,
  onClose,
  onSuccess,
  editGoal = null,
}) => {
  const { user } = useAuth();
  const isAdmin = hasAnyRole(user, ['Super Admin', 'Admin']);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  const [formData, setFormData] = useState({
    scope: 'user',
    metric_type: '',
    target_value: '',
    direction: 'maximize',
    period: 'monthly',
    period_start: '',
    period_end: '',
    title: '',
    description: '',
  });

  // Load metrics on open
  useEffect(() => {
    if (isOpen) {
      loadMetrics();

      // If editing, populate form
      if (editGoal) {
        setFormData({
          scope: editGoal.scope,
          metric_type: editGoal.metric_type,
          target_value: editGoal.target_value,
          direction: editGoal.direction,
          period: editGoal.period,
          period_start: editGoal.period_start,
          period_end: editGoal.period_end,
          title: editGoal.title,
          description: editGoal.description || '',
        });
      } else {
        // Reset form for new goal
        const { start, end } = goalService.calculatePeriodDates('monthly');
        setFormData({
          scope: 'user',
          metric_type: '',
          target_value: '',
          direction: 'maximize',
          period: 'monthly',
          period_start: goalService.formatDateForApi(start),
          period_end: goalService.formatDateForApi(end),
          title: '',
          description: '',
        });
      }
    }
  }, [isOpen, editGoal]);

  const loadMetrics = async () => {
    setLoadingMetrics(true);
    try {
      const response = await goalService.getMetrics();
      setMetrics(response.data.metrics);
    } catch (err) {
      console.error('Error loading metrics:', err);
      setError('Failed to load metrics');
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Update dates when period changes
  const handlePeriodChange = (period) => {
    const { start, end } = goalService.calculatePeriodDates(period);
    setFormData(prev => ({
      ...prev,
      period,
      period_start: goalService.formatDateForApi(start),
      period_end: goalService.formatDateForApi(end),
    }));
  };

  // Update direction when metric changes
  const handleMetricChange = (metricType) => {
    const metric = metrics.find(m => m.metric_type === metricType);
    if (metric) {
      setFormData(prev => ({
        ...prev,
        metric_type: metricType,
        direction: metric.default_direction,
        // Auto-generate title if empty
        title: prev.title || `${metric.label} Goal`,
      }));
    }
  };

  // Filter metrics based on scope
  const availableMetrics = metrics.filter(m => {
    if (formData.scope === 'user') return m.user_allowed;
    return m.firm_allowed;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.metric_type) {
      setError('Please select a metric');
      setLoading(false);
      return;
    }

    if (!formData.target_value || parseFloat(formData.target_value) < 0) {
      setError('Please enter a valid target value');
      setLoading(false);
      return;
    }

    if (!formData.title.trim()) {
      setError('Please enter a title for your goal');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        target_value: parseFloat(formData.target_value),
        user_id: formData.scope === 'user' ? user.id : undefined,
      };

      let result;
      if (editGoal) {
        result = await goalService.updateGoal(editGoal.id, payload);
      } else {
        result = await goalService.createGoal(payload);
      }

      onSuccess?.(result.data);
      onClose();
    } catch (err) {
      console.error('Error saving goal:', err);
      setError(err.response?.data?.detail || 'Failed to save goal');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedMetric = metrics.find(m => m.metric_type === formData.metric_type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-yellow-50 to-orange-50 p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-xl">
                <Target className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {editGoal ? 'Edit Goal' : 'Create New Goal'}
                </h2>
                <p className="text-sm text-gray-500">
                  Set a measurable target to track your progress
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Scope Selection - Only show if admin */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Goal Type
              </label>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, scope: 'user', metric_type: '' }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    formData.scope === 'user'
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">Personal Goal</div>
                  <div className="text-xs text-gray-500 mt-0.5">For your performance</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, scope: 'firm', metric_type: '' }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    formData.scope === 'firm'
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">Firm Goal</div>
                  <div className="text-xs text-gray-500 mt-0.5">For the whole team</div>
                </button>
              </div>
            </div>
          )}

          {/* Metric Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Metric to Track
            </label>
            {loadingMetrics ? (
              <div className="animate-pulse h-10 bg-gray-100 rounded-lg" />
            ) : (
              <select
                value={formData.metric_type}
                onChange={(e) => handleMetricChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                required
              >
                <option value="">Select a metric...</option>
                {availableMetrics.map((metric) => (
                  <option key={metric.metric_type} value={metric.metric_type}>
                    {metric.label}
                  </option>
                ))}
              </select>
            )}
            {selectedMetric && (
              <div className="mt-2 flex items-start space-x-2 text-sm text-gray-500">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{selectedMetric.description}</span>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Goal Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Increase billable hours this month"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              maxLength={200}
              required
            />
          </div>

          {/* Target Value & Direction */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Value {selectedMetric && `(${selectedMetric.unit})`}
              </label>
              <input
                type="number"
                value={formData.target_value}
                onChange={(e) => setFormData(prev => ({ ...prev, target_value: e.target.value }))}
                placeholder="0"
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Direction
              </label>
              <select
                value={formData.direction}
                onChange={(e) => setFormData(prev => ({ ...prev, direction: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="maximize">Maximize (reach or exceed)</option>
                <option value="minimize">Minimize (stay at or below)</option>
                <option value="target">Target (as close as possible)</option>
              </select>
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline-block h-4 w-4 mr-1" />
              Time Period
            </label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {['weekly', 'monthly', 'quarterly', 'yearly'].map((period) => (
                <button
                  key={period}
                  type="button"
                  onClick={() => handlePeriodChange(period)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                    formData.period === period
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {goalService.getPeriodLabel(period)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.period_start}
                  onChange={(e) => setFormData(prev => ({ ...prev, period_start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Date</label>
                <input
                  type="date"
                  value={formData.period_end}
                  onChange={(e) => setFormData(prev => ({ ...prev, period_end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>
          </div>

          {/* Description (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Add any notes or context for this goal..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>{editGoal ? 'Save Changes' : 'Create Goal'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGoalModal;
